import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  // Function to handle pending like after login
  const handlePendingLike = async () => {
    const pendingImageId = sessionStorage.getItem('pendingLikeImageId');
    if (pendingImageId) {
      try {
        await axios.post(`${API_BASE}/images/${pendingImageId}/like`, {}, {
          withCredentials: true
        });
        console.log('Pending like processed for image:', pendingImageId);
      } catch (error) {
        console.error('Error processing pending like:', error);
      } finally {
        sessionStorage.removeItem('pendingLikeImageId');
      }
    }
  };

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id found');
          navigate('/login', { replace: true });
          return;
        }

        // Exchange session_id for session_token
        const response = await axios.post(
          `${API_BASE}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        // Set user in context
        setUser(response.data.user);

        // Small delay to ensure cookie is set before navigation
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Re-check auth to ensure session is properly established
        await checkAuth();

        // Handle any pending like action
        await handlePendingLike();

        // Pick destination based on ?next=… (only allow internal paths).
        const search = new URLSearchParams(location.search);
        const rawNext = search.get('next') || '';
        const nextPath = rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '';
        const profileComplete = response.data.user?.profile_complete;
        if (!profileComplete) {
          navigate(
            nextPath ? `/complete-profile?next=${encodeURIComponent(nextPath)}` : '/complete-profile',
            { replace: true }
          );
        } else {
          navigate(nextPath || '/account', { replace: true });
        }

      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login', { replace: true });
      }
    };

    processSession();
  }, [location, navigate, setUser, checkAuth]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-gray-600 mx-auto mb-4" />
        <p className="text-gray-600">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;